import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  AlertCircle, 
  ChevronRight, 
  Plus, 
  Settings, 
  Pencil, 
  CreditCard, 
  ArrowLeft, 
  Shield, 
  DollarSign, 
  BarChart4, 
  Globe, 
  Clock, 
  X, 
  ChevronLeft,
  Info,
  Lock,
  Check,
  ExternalLink,
  HelpCircle,
  Home,
  ShoppingBag,
  Users
} from 'lucide-react';

import { useApp } from '../../context/AppContext';
import AjouterMethode from './AjouterMethode';

// Badge de statut de l'aggrégateur
const AggregatorStatus = ({ isConfigured }) => (
  <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
    isConfigured 
      ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20' 
      : 'bg-gray-100 text-gray-600 ring-1 ring-gray-400/20'
  }`}>
    {isConfigured ? (
      <>
        <CheckCircle size={12} className="text-green-600" />
        <span>Actif</span>
      </>
    ) : (
      <>
        <AlertCircle size={12} className="text-gray-500" />
        <span>Non configuré</span>
      </>
    )}
  </div>
);

// Card d'information avec style amélioré
const InfoCard = ({ children }) => (
  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
    <div className="flex items-start gap-3">
      <div className="p-1.5 bg-blue-100 rounded-full text-blue-600 mt-0.5">
        <Info size={16} />
      </div>
      <div className="text-sm text-blue-700">
        {children}
      </div>
    </div>
  </div>
);

// Fonctions utilitaires améliorées
const getProviderColor = (type) => {
  const colors = {
    stripe: 'bg-violet-50 text-violet-600 shadow-violet-100',
    paypal: 'bg-blue-50 text-blue-600 shadow-blue-100',
    googlepay: 'bg-green-50 text-green-600 shadow-green-100',
    binance: 'bg-yellow-50 text-yellow-600 shadow-yellow-100',
    fedapay: 'bg-orange-50 text-orange-600 shadow-orange-100',
    monero: 'bg-purple-50 text-purple-600 shadow-purple-100',
    flutterwave: 'bg-cyan-50 text-cyan-600 shadow-cyan-100',
    cinetpay: 'bg-teal-50 text-teal-600 shadow-teal-100'
  };
  return colors[type] || 'bg-gray-50 text-gray-600 shadow-gray-100';
};

const getIconComponent = (type) => {
  switch(type) {
    case 'fedapay':
      return <DollarSign className="w-6 h-6" />;
    case 'monero':
      return <Globe className="w-6 h-6" />;
    case 'binance':
      return <BarChart4 className="w-6 h-6" />;
    case 'flutterwave':
      return <CreditCard className="w-6 h-6" />;
    case 'cinetpay':
      return <DollarSign className="w-6 h-6" />;
    default:
      return <CreditCard className="w-6 h-6" />;
  }
};

// Carte d'aggrégateur améliorée avec animation
const AggregatorCard = ({ data, onAddMethod, onEditMethod, isFreePlan }) => {
  if (!data) return null;

  const handleCardClick = () => {
    if (isFreePlan) {
      // Pour le plan gratuit, on montre une alerte au lieu de permettre l'édition
      onAddMethod(data); // Cette fonction affichera l'alerte de fonctionnalité premium
    } else if (data.isConfigured) {
      onEditMethod(data);
    } else {
      onAddMethod(data);
    }
  };

  // Masquer partiellement la clé API
  const maskApiKey = (key) => {
    if (!key) return 'N/A';
    const lastFour = key.slice(-4);
    return `•••••••${lastFour}`;
  };

  return (
    <div 
      onClick={handleCardClick}
      className={`group relative bg-white rounded-2xl overflow-hidden transition-all duration-300 transform hover:-translate-y-1 ${
        data.isConfigured 
          ? 'border border-gray-200 hover:border-gray-300 hover:shadow-xl' 
          : 'border border-dashed border-gray-200 hover:border-blue-200 hover:bg-blue-50'
      }`}
    >
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`flex items-center justify-center w-12 h-12 rounded-xl shadow-md ${getProviderColor(data.type)} group-hover:scale-110 transition-transform duration-300`}>
              {getIconComponent(data.type)}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <h3 className="text-base font-semibold text-gray-900">
                  {data.name}
                </h3>
                <AggregatorStatus isConfigured={data.isConfigured} />
              </div>
              <p className="text-sm text-gray-500">
                {data.isConfigured 
                  ? `Mode ${data.mode === 'production' ? 'Production' : 'Test'}`
                  : "Configurer pour accepter les paiements"}
              </p>
            </div>
          </div>

          <div className="text-gray-400 group-hover:text-gray-600 transition-colors group-hover:translate-x-1 transform transition-transform duration-300">
            <ChevronRight size={20} />
          </div>
        </div>

        {data.isConfigured && (
          <div className="mt-6 pt-5 border-t border-gray-100">
            <div className="grid gap-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 flex items-center">
                  <Lock className="w-3.5 h-3.5 mr-1.5" />
                  Clé API
                </span>
                <code className="px-2.5 py-1 bg-gray-50 rounded-lg font-mono text-gray-600 text-xs">
                  {maskApiKey(data.key)}
                </code>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 flex items-center">
                  <Clock className="w-3.5 h-3.5 mr-1.5" />
                  Dernière mise à jour
                </span>
                <span className="text-gray-600">
                  {data.lastUpdate || 'Il y a quelques jours'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="w-full px-6 py-4 bg-gray-50 border-t border-gray-100">
        {isFreePlan ? (
          <button className="flex items-center justify-center w-full gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 bg-white rounded-xl shadow-sm border border-gray-200 opacity-70 cursor-default">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>Plan Premium requis</span>
          </button>
        ) : data.isConfigured ? (
          <button className="flex items-center justify-center w-full gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white rounded-xl shadow-sm border border-gray-200 transition-all duration-300 hover:border-gray-300 hover:shadow-md">
            <Pencil size={16} />
            <span>Modifier</span>
          </button>
        ) : (
          <button
            className="flex items-center justify-center w-full gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl shadow-sm transition-all duration-300 hover:bg-blue-700 hover:shadow-md"
          >
            <Plus size={16} />
            <span>Configurer</span>
          </button>
        )}
      </div>
    </div>
  );
};

// Carte de récapitulatif
const SummaryCard = ({ activeCount, totalCount }) => (
  <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-5 text-white shadow-lg mb-6 relative overflow-hidden">
    <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -translate-x-10 -translate-y-10"></div>
    <div className="absolute bottom-0 left-0 w-32 h-32 bg-white opacity-10 rounded-full translate-x-10 translate-y-10"></div>
    
    <div className="relative z-10">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-white/20 rounded-lg">
          <CreditCard className="w-5 h-5" />
        </div>
        <h3 className="text-lg font-semibold">Moyens de paiement</h3>
      </div>
      
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/80 text-sm mb-1">Méthodes actives</p>
          <p className="text-2xl font-bold flex items-baseline">
            {activeCount}
            <span className="text-sm text-white/60 ml-1">sur {totalCount}</span>
          </p>
        </div>
        
        <div className="p-3 bg-white/10 rounded-lg text-center">
          <div className="font-bold text-xl">{Math.round((activeCount/totalCount) * 100) || 0}%</div>
          <div className="text-xs text-white/80">Configuré</div>
        </div>
      </div>
    </div>
  </div>
);

// Composant pour les instructions
const Instructions = () => (
  <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
    <h3 className="text-lg font-medium text-gray-900 mb-3">Comment ça marche</h3>
    
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium">
          1
        </div>
        <div>
          <p className="text-sm text-gray-600">
            Configurez au moins un moyen de paiement pour commencer à percevoir des paiements
          </p>
        </div>
      </div>
      
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium">
          2
        </div>
        <div>
          <p className="text-sm text-gray-600">
            Vous devez obtenir vos clés API auprès de chaque fournisseur de paiement
          </p>
        </div>
      </div>
      
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium">
          3
        </div>
        <div>
          <p className="text-sm text-gray-600">
            Les fonds seront versés selon les modalités de chaque fournisseur de paiement
          </p>
        </div>
      </div>
    </div>
    
    <button className="flex items-center gap-1.5 text-blue-600 font-medium text-sm mt-4">
      En savoir plus
      <ExternalLink className="w-3.5 h-3.5" />
    </button>
  </div>
);

// Composant principal amélioré
const PaymentsSheetsIndex = () => {
  const navigate = useNavigate();
  const { 
    userInfos, 
    allAggregators, 
    userActiveAggregators,
    isLoading 
  } = useApp();
  
  const [scrolled, setScrolled] = useState(false);
  const [aggregators, setAggregators] = useState({});
  const [showInstructions, setShowInstructions] = useState(true);
  
  // Déboggage pour suivre les données chargées
  useEffect(() => {
    console.log("État du contexte des agrégateurs:", {
      userInfos: userInfos ? "Chargé" : "Non chargé",
      typePlan: userInfos?.type_plan || "Non défini",
      allAggregators: allAggregators 
        ? `Type: ${typeof allAggregators} | ${Array.isArray(allAggregators) 
          ? `Tableau avec ${allAggregators.length} éléments` 
          : `Objet avec clés: ${Object.keys(allAggregators).join(', ')}`}` 
        : "Non chargé",
      userActiveAggregators: userActiveAggregators 
        ? `Type: ${typeof userActiveAggregators} | ${Array.isArray(userActiveAggregators) 
          ? `Tableau avec ${userActiveAggregators.length} éléments` 
          : `Objet avec clés: ${Object.keys(userActiveAggregators).join(', ')}`}` 
        : "Non chargé",
      isLoading
    });
    
    // Afficher la structure détaillée pour le débogage
    if (allAggregators) {
      console.log("Structure détaillée de allAggregators:", 
        Array.isArray(allAggregators) 
          ? allAggregators.slice(0, 2) // Montrer les 2 premiers éléments si c'est un tableau
          : Object.fromEntries(Object.entries(allAggregators).slice(0, 2)) // Montrer les 2 premières entrées si c'est un objet
      );
    }
    
    if (userActiveAggregators) {
      console.log("Structure détaillée de userActiveAggregators:", 
        Array.isArray(userActiveAggregators) 
          ? userActiveAggregators.slice(0, 2) 
          : Object.fromEntries(Object.entries(userActiveAggregators).slice(0, 2))
      );
    }
  }, [userInfos, allAggregators, userActiveAggregators, isLoading]);
  
  // Construction des aggrégateurs avec les données réelles de la base de données, sans fallback sur des données simulées
  useEffect(() => {
    // Fonction de construction des aggrégateurs
    function buildAggregators() {
      // Si nous sommes en attente de chargement, on ne fait rien
      if (!allAggregators) {
        console.log("En attente des données d'agrégateurs...");
        return;
      }
      
      // Utiliser exclusivement les données réelles de Firebase
      const aggregatorsObj = {};
      
      // Traiter les différents formats possibles pour allAggregators
      const aggregatorEntries = [];
      
      // Si allAggregators est un objet avec structure {fedapay: {...}, monero: {...}}
      if (!Array.isArray(allAggregators) && typeof allAggregators === 'object') {
        console.log("Format objet détecté pour allAggregators:", Object.keys(allAggregators));
        
        // Convertir l'objet en entrées pour le traitement
        Object.entries(allAggregators).forEach(([key, data]) => {
          aggregatorEntries.push({
            key: key,
            name: data.name || key,
            ...data
          });
        });
      } 
      // Si allAggregators est un tableau d'objets
      else if (Array.isArray(allAggregators)) {
        console.log("Format tableau détecté pour allAggregators");
        aggregatorEntries.push(...allAggregators);
      }
      else {
        console.error("Format non supporté pour allAggregators:", allAggregators);
        return;
      }
      
      // Parcours des agrégateurs disponibles
      aggregatorEntries.forEach(agg => {
        if (!agg?.key) return;
        
        console.log(`Traitement de l'agrégateur: ${agg.key}`);

        // Vérification du format des agrégateurs actifs 
        // Dans la db.json, userAggregatorKeys est un objet avec des clés par type d'agrégateur
        let activeAgg = null;
        let apiKey = null;
        let updatedTimestamp = null;
        let mode = "production";
        
        // Si userActiveAggregators est un tableau (comme dans le context)
        if (Array.isArray(userActiveAggregators)) {
          activeAgg = userActiveAggregators.find(active => active.key === agg.key);
          if (activeAgg) {
            apiKey = activeAgg.userKeys?.apiKey;
            updatedTimestamp = activeAgg.updatedAt;
            mode = activeAgg.userKeys?.mode || "production";
          }
        } 
        // Si userActiveAggregators est un objet (comme dans db.json)
        else if (userActiveAggregators && typeof userActiveAggregators === 'object') {
          // Logique pour différentes structures d'userActiveAggregators
          
          // Structure 1: {fedapay: {isActive: true, keys: {apiKey: 'xyz'}}} (directe)
          if (userActiveAggregators[agg.key]) {
            const aggData = userActiveAggregators[agg.key];
            if (aggData.isActive) {
              activeAgg = aggData;
              apiKey = aggData.keys?.apiKey;
              updatedTimestamp = aggData.updatedAt;
              mode = aggData.keys?.mode || "production";
            }
          } 
          // Structure 2: {"604915269": {fedapay: {isActive: true, keys: {}}}} (par utilisateur)
          else {
            // Parcourir les IDs utilisateurs pour trouver l'agrégateur actif
            const userIds = Object.keys(userActiveAggregators);
            for (let i = 0; i < userIds.length; i++) {
              const userId = userIds[i];
              const userAggs = userActiveAggregators[userId];
              
              if (userAggs && userAggs[agg.key] && userAggs[agg.key].isActive) {
                activeAgg = userAggs[agg.key];
                apiKey = activeAgg.keys?.apiKey;
                updatedTimestamp = activeAgg.updatedAt;
                mode = activeAgg.keys?.mode || "production";
                console.log(`Agrégateur ${agg.key} actif trouvé pour l'utilisateur ${userId}`);
                break;
              }
            }
          }
        }
  
        // Formatage des données pour l'affichage
        aggregatorsObj[agg.key] = {
          type: agg.key,
          isConfigured: !!activeAgg,
          name: agg.name || 'Unknown Provider',
          key: apiKey || null,
          mode: mode,
          // Formatage de la date de dernière mise à jour
          lastUpdate: updatedTimestamp 
            ? formatLastUpdate(updatedTimestamp) 
            : "Récemment",
          id: agg.key
        };
      });
  
      // Seulement mettre à jour si les données ont changé pour éviter une boucle infinie
      setAggregators(prev => {
        // Vérifier si les données sont les mêmes pour éviter des re-renders inutiles
        if (JSON.stringify(prev) === JSON.stringify(aggregatorsObj)) {
          return prev; // Retourner les données précédentes si rien n'a changé
        }
        return aggregatorsObj; // Sinon, mettre à jour avec les nouvelles données
      });
    }
  
    // Formater la date relative
    function formatLastUpdate(timestamp) {
      if (!timestamp) return "N/A";
      
      const now = Date.now();
      const diff = now - timestamp;
      
      // Conversion en jours/heures/minutes
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (days > 0) {
        return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
      } else if (hours > 0) {
        return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`;
      } else {
        return `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
      }
    }
    
    // Appeler la fonction buildAggregators quand les données changent
    buildAggregators();
  }, [allAggregators, userActiveAggregators]);

  const handleScroll = (e) => {
    setScrolled(e.target.scrollTop > 0);
  };

  // Vérifier si l'utilisateur est sur un plan gratuit
  const isFreePlan = userInfos?.type_plan === 'gratuit';
  
  // Affiche une alerte pour les utilisateurs du plan gratuit
  const showUpgradeAlert = () => {
    // Créer un élément d'alerte personnalisé avec z-index très élevé
    const alertElement = document.createElement('div');
    alertElement.className = 'fixed inset-0 flex items-center justify-center p-4';
    // Style inline pour s'assurer qu'il est au-dessus de tout
    alertElement.style.zIndex = '99999';
    
    alertElement.innerHTML = `
      <div class="fixed inset-0 bg-black bg-opacity-60" style="z-index: 99998;"></div>
      <div class="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden relative" style="z-index: 99999;">
        <div class="p-6">
          <div class="bg-blue-50 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-center text-gray-900 mb-2">Fonctionnalité Premium</h3>
          <p class="text-center text-gray-600 mb-6">
            La configuration des moyens de paiement n'est pas disponible avec le plan gratuit. 
            Passez à un plan supérieur pour débloquer cette fonctionnalité.
          </p>
          <div class="flex justify-center gap-3">
            <button id="upgrade-dismiss" class="px-4 py-2 bg-gray-100 text-gray-800 rounded-xl font-medium">Fermer</button>
            <button id="upgrade-btn" class="px-4 py-2 bg-blue-600 text-white rounded-xl font-medium">Voir les plans</button>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(alertElement);
    
    // Gérer les événements des boutons après un court délai pour s'assurer que les éléments sont dans le DOM
    setTimeout(() => {
      document.getElementById('upgrade-dismiss')?.addEventListener('click', () => {
        document.body.removeChild(alertElement);
      });
      
      document.getElementById('upgrade-btn')?.addEventListener('click', () => {
        document.body.removeChild(alertElement);
        navigate('/Plans');
      });
    }, 100);
  };
  
  const handleAddMethod = (data) => {
    if (isFreePlan) {
      showUpgradeAlert();
    } else {
      // Naviguer vers la page de configuration avec les paramètres
      navigate(`/ModifierKeyIndex?nom=${data.name}&id=${data.id || data.type}&action=add`);
    }
  };
  
  const handleEditMethod = (data) => {
    if (isFreePlan) {
      showUpgradeAlert();
    } else {
      // Naviguer vers la page de modification avec les paramètres
      navigate(`/ModifierKeyIndex?nom=${data.name}&id=${data.id || data.type}&key=${data.key}&action=edit`);
    }
  };
  
  // Calculer les statistiques
  const activeCount = Object.values(aggregators).filter(agg => agg.isConfigured).length;
  const totalCount = Object.values(aggregators).length;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Contenu principal */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Carte de récapitulatif */}
        <SummaryCard activeCount={activeCount} totalCount={totalCount} />
        
        {/* Instructions (conditionnelles) */}
        {showInstructions && <Instructions />}
        
        {/* Info Card */}
        {isFreePlan ? (
          <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="p-1.5 bg-yellow-100 rounded-full text-yellow-600 mt-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-yellow-700 font-medium mb-1">Plan Gratuit</p>
                <p className="text-sm text-yellow-600">
                  La configuration des moyens de paiement n'est pas disponible avec le plan gratuit. 
                  <button 
                    onClick={() => navigate('/Plans')}
                    className="text-blue-600 font-medium ml-1 hover:underline"
                  >
                    Passez à un plan supérieur
                  </button> pour débloquer cette fonctionnalité.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <InfoCard>
            Configurez vos méthodes de paiement pour permettre à vos clients de payer via leurs moyens préférés. 
            Vous aurez besoin des clés API de chaque fournisseur de paiement.
          </InfoCard>
        )}
        
        {/* Liste des aggrégateurs */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Méthodes disponibles</h2>
          
          {/* Indicateur de chargement */}
          {isLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          )}
          
          {/* Message quand aucun agrégateur n'est disponible */}
          {!isLoading && Object.keys(aggregators).length === 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
              <div className="bg-gray-100 p-3 rounded-full inline-flex mb-4">
                <Info className="h-6 w-6 text-gray-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun moyen de paiement disponible</h3>
              <p className="text-gray-600 mb-4">
                Les méthodes de paiement ne sont pas encore configurées sur le système.
                Veuillez réessayer plus tard ou contacter le support.
              </p>
            </div>
          )}
          
          {/* Liste des agrégateurs disponibles */}
          {Object.values(aggregators).map((aggregator, index) => (
            <div key={`${aggregator.type}-${index}`} className="relative">
              {isFreePlan && (
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-gray-100 to-transparent z-10 rounded-b-2xl flex items-end justify-center p-4">
                  <div className="bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-200 flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span className="text-xs font-medium text-gray-700">Fonctionnalité Premium</span>
                  </div>
                </div>
              )}
              <AggregatorCard 
                data={aggregator}
                onAddMethod={handleAddMethod}
                onEditMethod={handleEditMethod}
                isFreePlan={isFreePlan}
              />
            </div>
          ))}
        </div>
        
        {/* Carte d'aide */}
        <div className="mt-8 bg-gray-100 rounded-2xl p-6 border border-gray-200">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-full text-blue-600">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-medium text-gray-900 mb-1">Besoin d'aide ?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Si vous avez des questions sur la configuration de vos moyens de paiement, notre équipe est là pour vous aider.
              </p>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                Contacter le support
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Ajout d'une méthode de paiement (bouton flottant) - position recalculée pour éviter la bottom bar */}
      <div className="fixed right-6 z-[999]" style={{ bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))' }}>
        <button 
          onClick={() => handleAddMethod({ name: 'Nouvelle méthode', id: 'new' })}
          className={`w-12 h-12 rounded-full text-white shadow-lg transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center ${isFreePlan ? 'bg-gray-400 hover:bg-gray-500' : 'bg-blue-600 hover:bg-blue-700 hover:shadow-xl'}`}
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>
      
      {/* Spacer plus grand pour éviter que le contenu ne soit masqué par la bottom bar */}
      <div className="h-32"></div>
      
      {/* Navigation hyperlinks simples */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-[999999]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="max-w-lg mx-auto flex justify-between">
          <a 
            href="/IndexEspace"
            className="flex flex-col items-center p-2 flex-1 text-gray-500 hover:text-blue-600 active:bg-gray-100"
          >
            <Home className="w-6 h-6" />
            <span className="text-xs mt-1">Accueil</span>
          </a>
          
          <a 
            href="/ProductsList"
            className="flex flex-col items-center p-2 flex-1 text-gray-500 hover:text-blue-600 active:bg-gray-100"
          >
            <ShoppingBag className="w-6 h-6" />
            <span className="text-xs mt-1">Produits</span>
          </a>
          
          <a 
            href="/GroupeListe"
            className="flex flex-col items-center p-2 flex-1 text-gray-500 hover:text-blue-600 active:bg-gray-100"
          >
            <Users className="w-6 h-6" />
            <span className="text-xs mt-1">Groupes</span>
          </a>
          
          <a 
            href="/Statistics"
            className="flex flex-col items-center p-2 flex-1 text-gray-500 hover:text-blue-600 active:bg-gray-100"
          >
            <BarChart4 className="w-6 h-6" />
            <span className="text-xs mt-1">Stats</span>
          </a>
          
          <a 
            href="/PaymentsSheetsIndex"
            className="flex flex-col items-center p-2 flex-1 text-blue-600"
          >
            <Settings className="w-6 h-6" />
            <span className="text-xs mt-1">Réglages</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default PaymentsSheetsIndex;